import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, notificationAPI, messageAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const pollRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (storedUser && token) {
        // Verify with backend
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      // Clear invalid auth
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCounts = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [notifRes, msgRes] = await Promise.all([
        notificationAPI.getUnreadCount(),
        messageAPI.getUnreadCount(),
      ]);
      setUnreadNotifications(notifRes.data.unread_count);
      setUnreadMessages(msgRes.data.unread_count);
    } catch (error) {
      // Silently fail - don't disrupt auth
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Poll for unread counts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCounts();
      pollRef.current = setInterval(fetchUnreadCounts, 30000);
      return () => clearInterval(pollRef.current);
    } else {
      setUnreadNotifications(0);
      setUnreadMessages(0);
    }
  }, [isAuthenticated, fetchUnreadCounts]);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token, user: userData } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const signup = async (data) => {
    const response = await authAPI.signup(data);
    const { token, user: userData } = response.data;

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await authAPI.exchangeSession(sessionId);
    const userData = response.data;

    localStorage.setItem('token', userData.session_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setUnreadNotifications(0);
    setUnreadMessages(0);
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        unreadNotifications,
        unreadMessages,
        login,
        signup,
        loginWithGoogle,
        handleOAuthCallback,
        logout,
        updateUser,
        checkAuth,
        fetchUnreadCounts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
